import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import {
  ShareVehicleByVehicleDto,
  ShareVehicleMemberDto,
  VehicleTableRow,
} from '../../../../../core/models/vehicle/vehicle-list.model';
import { MapperVehicleListService } from '../../../../../core/mappers/vehicle/mapper-vehicle-list.service';
import { ApiVehicleListService } from '../../../../../core/services/api/vehicle/api-vehicle-list.service';
import { ModalAdminActivityHistoryComponent } from '../../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';
import { ModalLineOaMemberComponent } from '../../../modals/line-oa/modal-line-oa-member/modal-line-oa-member.component';
import { CityParkingUserLineResponse } from '../../../../../core/models/lineOA.model';

@Component({
  selector: 'app-vehicle-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    ModalAdminActivityHistoryComponent,
    ModalLineOaMemberComponent,
  ],
  templateUrl: './vehicle-list.component.html',
  styleUrl: './vehicle-list.component.css',
  providers: [DatePipe],
})
export class VehicleListComponent implements OnInit {
  private readonly apiService = inject(ApiVehicleListService);
  private readonly mapper = inject(MapperVehicleListService);
  private readonly toast = inject(ToastService);
  private readonly datePipe = inject(DatePipe);

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  activityHistoryVehicleId = 0;

  vehicles: VehicleTableRow[] = [];
  searchText = '';
  currentPage = 1;
  pageSize = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems = 0;
  totalPages = 1;
  isLoading = false;
  lastFetchedTime: Date | null = null;
  isShareModalOpen = false;
  isShareDetailLoading = false;
  selectedVehicleShare: ShareVehicleByVehicleDto | null = null;

  ngOnInit(): void {
    this.loadTable();
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(this.totalPages, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  formatDateTime(value: string | null): string {
    if (!value) return '-';
    return this.datePipe.transform(value, 'dd/MM/yyyy HH:mm') || '-';
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  onRefresh(): void {
    this.searchText = '';
    this.currentPage = 1;
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize = Number(element.value);
    this.currentPage = 1;
    this.loadTable();
  }

  private loadTable(): void {
    this.isLoading = true;
    this.apiService
      .getVehicleForTable({
        page: this.currentPage,
        pageSize: this.pageSize,
        searchText: this.searchText.trim() || undefined,
      })
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTableFromDto(res);
          this.vehicles = mapped.items;
          this.totalItems = mapped.total;
          this.totalPages = mapped.totalPages || Math.ceil(mapped.total / (this.pageSize || 1));
          this.isLoading = false;
          this.lastFetchedTime = new Date();
        },
        error: (err) => {
          console.error('[API Error] getVehicleForTable:', err);
          this.vehicles = [];
          this.totalItems = 0;
          this.totalPages = 1;
          this.isLoading = false;
          this.toast.error('ไม่สามารถโหลดข้อมูลทะเบียนรถได้');
        },
      });
  }

  get activeShareMembers(): ShareVehicleMemberDto[] {
    return (this.selectedVehicleShare?.activeGroup?.members ?? []).filter(
      (member) => member.disable !== true,
    );
  }

  openShareMembers(vehicle: VehicleTableRow): void {
    if (!vehicle.hasActiveShare || this.isShareDetailLoading) return;

    this.isShareModalOpen = true;
    this.isShareDetailLoading = true;
    this.selectedVehicleShare = {
      vehicleId: vehicle.id,
      plateNumber: vehicle.plateNumber,
      activeGroup: null,
    };

    this.apiService.getShareVehicleByVehicleId(vehicle.id).subscribe({
      next: (response) => {
        this.selectedVehicleShare = response;
        this.isShareDetailLoading = false;
      },
      error: (err) => {
        console.error('[API Error] getShareVehicleByVehicleId:', err);
        this.isShareDetailLoading = false;
        this.isShareModalOpen = false;
        this.selectedVehicleShare = null;
        this.toast.error('ไม่สามารถโหลดข้อมูลสมาชิกที่แชร์รถได้');
      },
    });
  }

  closeShareModal(): void {
    this.isShareModalOpen = false;
    this.isShareDetailLoading = false;
    this.selectedVehicleShare = null;
  }

  openActivityHistory(vehicleId: number): void {
    if (!vehicleId || vehicleId <= 0) return;
    this.activityHistoryVehicleId = vehicleId;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
    this.activityHistoryVehicleId = 0;
  }

  isOwnerModalOpen = false;
  selectedOwner: CityParkingUserLineResponse | null = null;
  private brokenPictureIds = new Set<number>();

  openOwnerProfile(vehicle: VehicleTableRow): void {
    if (!vehicle.cpUserId) {
      this.toast.error('ไม่พบข้อมูลเจ้าของรถ');
      return;
    }
    this.selectedOwner = this.mapper.mapOwnerMember(vehicle);
    this.isOwnerModalOpen = true;
  }

  closeOwnerProfile(): void {
    this.isOwnerModalOpen = false;
    this.selectedOwner = null;
    this.loadTable();
  }

  clearSearch(): void {
    if (!this.searchText) return;
    this.searchText = '';
    this.onSearch();
  }

  isPictureBroken(vehicleId: number): boolean {
    return this.brokenPictureIds.has(vehicleId);
  }

  onPictureError(vehicleId: number): void {
    this.brokenPictureIds.add(vehicleId);
  }
}
